export const getStyles = (primaryColor: string = '#14b8a6') => `
  /* Reset all styles for the widget to prevent host page interference */
  .librebot-widget,
  .librebot-widget *,
  .librebot-widget *::before,
  .librebot-widget *::after,
  .librebot-fab,
  .librebot-fab *,
  .librebot-modal,
  .librebot-modal * {
    box-sizing: border-box !important;
    margin: 0;
    padding: 0;
    border: 0;
    outline: none;
    font: inherit;
    vertical-align: baseline;
    text-decoration: none;
  }

  .librebot-widget {
    --lb-primary: ${primaryColor};
    --lb-primary-hover: color-mix(in srgb, ${primaryColor} 85%, white);
    --lb-bg: #0f0f0f;
    --lb-bg-secondary: #1a1a1a;
    --lb-text: #ffffff;
    --lb-text-secondary: #9ca3af;
    --lb-border: #2a2a2a;
    font-family: 'Roboto Mono', -apple-system, BlinkMacSystemFont, 'Segoe UI', monospace !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
    color: var(--lb-text);
  }

  .librebot-widget.light {
    --lb-bg: #ffffff;
    --lb-bg-secondary: #f5f5f5;
    --lb-text: #1a1a1a;
    --lb-text-secondary: #666666;
    --lb-border: #e0e0e0;
  }

  .librebot-fab {
    all: unset !important;
    position: fixed !important;
    bottom: 20px !important;
    right: 20px !important;
    width: 56px !important;
    height: 56px !important;
    border-radius: 50% !important;
    background: var(--lb-primary) !important;
    border: none !important;
    outline: none !important;
    cursor: pointer !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: transform 0.2s, box-shadow 0.2s !important;
    z-index: 999998 !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    box-sizing: border-box !important;
  }

  .librebot-fab:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
  }

  .librebot-fab:focus,
  .librebot-fab:active {
    outline: none !important;
    border: none !important;
  }

  .librebot-fab svg {
    width: 24px !important;
    height: 24px !important;
    fill: white !important;
    border: none !important;
    outline: none !important;
    background: transparent !important;
  }

  .librebot-fab.left {
    right: auto !important;
    left: 20px !important;
  }

  .librebot-modal {
    position: fixed;
    bottom: 90px;
    right: 20px;
    width: 380px;
    max-width: calc(100vw - 40px);
    height: 520px;
    max-height: calc(100vh - 120px);
    background: var(--lb-bg);
    border: 1px solid var(--lb-border);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 999999;
    opacity: 0;
    transform: translateY(20px) scale(0.95);
    transition: opacity 0.2s, transform 0.2s;
    pointer-events: none;
  }

  .librebot-modal.open {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  .librebot-modal.left {
    right: auto;
    left: 20px;
  }

  .librebot-header {
    padding: 16px;
    border-bottom: 1px solid var(--lb-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .librebot-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .librebot-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--lb-primary);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .librebot-avatar svg {
    width: 24px;
    height: 24px;
    fill: white;
  }

  .librebot-title {
    font-weight: 600;
    color: var(--lb-text);
    margin: 0;
    font-size: 16px;
  }

  .librebot-subtitle {
    font-size: 12px;
    color: var(--lb-text-secondary);
    margin: 0;
  }

  .librebot-close {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--lb-text-secondary);
    transition: background 0.2s;
  }

  .librebot-close:hover {
    background: var(--lb-bg-secondary);
  }

  .librebot-chat-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .librebot-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .librebot-booking-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .librebot-message {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 16px;
    word-wrap: break-word;
  }

  .librebot-message.user {
    align-self: flex-end;
    background: var(--lb-primary);
    color: white;
    border-bottom-right-radius: 4px;
  }

  .librebot-message.assistant {
    align-self: flex-start;
    background: var(--lb-bg-secondary);
    color: var(--lb-text);
    border-bottom-left-radius: 4px;
  }

  /* Inline code */
  .librebot-inline-code {
    background: rgba(0, 0, 0, 0.3);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Roboto Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 12px;
    color: #f0f0f0;
  }

  .librebot-widget.light .librebot-inline-code {
    background: rgba(0, 0, 0, 0.08);
    color: #1a1a1a;
  }

  /* Code blocks */
  .librebot-code-block {
    background: #0d1117;
    padding: 12px 14px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 10px 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
  }

  .librebot-widget.light .librebot-code-block {
    background: #f6f8fa;
    border-color: rgba(0, 0, 0, 0.1);
  }

  .librebot-code-block code {
    font-family: 'Roboto Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
    color: #e6edf3;
    background: none;
    padding: 0;
    white-space: pre;
    display: block;
  }

  .librebot-widget.light .librebot-code-block code {
    color: #24292f;
  }

  .librebot-code-block[data-language]::before {
    content: attr(data-language);
    position: absolute;
    top: 6px;
    right: 10px;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    font-family: 'Roboto Mono', monospace;
  }

  .librebot-widget.light .librebot-code-block[data-language]::before {
    color: rgba(0, 0, 0, 0.3);
  }

  /* Headers */
  .librebot-h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 12px 0 8px 0;
    color: var(--lb-text);
  }

  .librebot-h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 10px 0 6px 0;
    color: var(--lb-text);
  }

  .librebot-h4 {
    font-size: 13px;
    font-weight: 600;
    margin: 8px 0 4px 0;
    color: var(--lb-text);
  }

  /* Lists */
  .librebot-ul, .librebot-ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  .librebot-ul {
    list-style-type: disc;
  }

  .librebot-ol {
    list-style-type: decimal;
  }

  .librebot-li, .librebot-li-ordered {
    margin: 4px 0;
    padding-left: 4px;
    line-height: 1.5;
  }

  /* Links */
  .librebot-link {
    color: var(--lb-primary);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s;
  }

  .librebot-link:hover {
    border-bottom-color: var(--lb-primary);
  }

  /* Blockquotes */
  .librebot-blockquote {
    border-left: 3px solid var(--lb-primary);
    margin: 8px 0;
    padding: 4px 12px;
    color: var(--lb-text-secondary);
    font-style: italic;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 0 6px 6px 0;
  }

  .librebot-widget.light .librebot-blockquote {
    background: rgba(0, 0, 0, 0.04);
  }

  /* Horizontal rule */
  .librebot-hr {
    border: none;
    border-top: 1px solid var(--lb-border);
    margin: 12px 0;
  }

  /* Strong and emphasis */
  .librebot-message strong {
    font-weight: 600;
  }

  .librebot-message em {
    font-style: italic;
  }

  /* Legacy support for old code elements */
  .librebot-message code:not(.librebot-inline-code) {
    background: rgba(0, 0, 0, 0.2);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 13px;
  }

  .librebot-message pre:not(.librebot-code-block) {
    background: rgba(0, 0, 0, 0.3);
    padding: 10px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;
  }

  .librebot-message pre:not(.librebot-code-block) code {
    background: none;
    padding: 0;
  }

  .librebot-typing {
    display: flex;
    gap: 4px;
    padding: 12px 16px;
    align-self: flex-start;
    background: var(--lb-bg-secondary);
    border-radius: 16px;
    border-bottom-left-radius: 4px;
  }

  .librebot-typing span {
    width: 8px;
    height: 8px;
    background: var(--lb-text-secondary);
    border-radius: 50%;
    animation: librebot-bounce 1.4s infinite ease-in-out;
  }

  .librebot-typing span:nth-child(1) { animation-delay: 0s; }
  .librebot-typing span:nth-child(2) { animation-delay: 0.2s; }
  .librebot-typing span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes librebot-bounce {
    0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }

  /* Sources display */
  .librebot-sources {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid var(--lb-border);
  }

  .librebot-sources-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--lb-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .librebot-source-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    font-size: 11px;
    color: var(--lb-text-secondary);
  }

  .librebot-widget.light .librebot-source-tag {
    background: rgba(0, 0, 0, 0.06);
  }

  .librebot-source-relevance {
    color: var(--lb-primary);
    font-weight: 500;
  }

  .librebot-input-area {
    padding: 12px 16px;
    border-top: 1px solid var(--lb-border);
    display: flex;
    gap: 8px;
  }

  .librebot-input {
    flex: 1;
    padding: 10px 14px;
    border: 1px solid var(--lb-border);
    border-radius: 24px;
    background: var(--lb-bg-secondary);
    color: var(--lb-text);
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }

  .librebot-input:focus {
    border-color: var(--lb-primary);
  }

  .librebot-input::placeholder {
    color: var(--lb-text-secondary);
  }

  .librebot-send {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: var(--lb-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
  }

  .librebot-send:hover {
    background: var(--lb-primary-hover);
  }

  .librebot-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .librebot-send svg {
    width: 18px;
    height: 18px;
    fill: white;
  }

  .librebot-welcome {
    text-align: center;
    padding: 20px;
    color: var(--lb-text-secondary);
  }

  .librebot-welcome-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    background: var(--lb-primary);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .librebot-welcome-icon svg {
    width: 28px;
    height: 28px;
    fill: white;
  }

  .librebot-powered {
    text-align: center;
    padding: 8px;
    font-size: 11px;
    color: var(--lb-text-secondary);
    border-top: 1px solid var(--lb-border);
  }

  .librebot-powered a {
    color: var(--lb-primary);
    text-decoration: none;
  }

  .librebot-powered a:hover {
    text-decoration: underline;
  }

  /* RTL Support */
  .librebot-widget.rtl {
    direction: rtl;
    text-align: right;
  }

  .librebot-widget.rtl .librebot-message.user {
    align-self: flex-start;
    border-bottom-right-radius: 16px;
    border-bottom-left-radius: 4px;
  }

  .librebot-widget.rtl .librebot-message.assistant {
    align-self: flex-end;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 4px;
  }

  .librebot-widget.rtl .librebot-typing {
    align-self: flex-end;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 4px;
  }

  .librebot-widget.rtl .librebot-header-content {
    flex-direction: row-reverse;
  }

  .librebot-widget.rtl .librebot-input {
    text-align: right;
  }

  .librebot-widget.rtl .librebot-input-area {
    flex-direction: row-reverse;
  }

  .librebot-widget.rtl .librebot-ul,
  .librebot-widget.rtl .librebot-ol {
    padding-left: 0;
    padding-right: 20px;
  }

  .librebot-widget.rtl .librebot-blockquote {
    border-left: none;
    border-right: 3px solid var(--lb-primary);
    border-radius: 6px 0 0 6px;
  }

  /* Booking Mode Styles */
  .librebot-booking {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .librebot-booking-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--lb-border);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .librebot-booking-back {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--lb-text-secondary);
    transition: background 0.2s;
  }

  .librebot-booking-back:hover {
    background: var(--lb-bg-secondary);
  }

  .librebot-booking-back svg {
    width: 20px;
    height: 20px;
  }

  .librebot-booking-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--lb-text);
    margin: 0;
  }

  .librebot-booking-content {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .librebot-booking-section {
    margin-bottom: 20px;
  }

  .librebot-booking-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--lb-text);
    margin-bottom: 8px;
  }

  .librebot-date-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--lb-border);
    border-radius: 8px;
    background: var(--lb-bg-secondary);
    color: var(--lb-text);
    font-size: 14px;
    outline: none;
    cursor: pointer;
  }

  .librebot-date-input:focus {
    border-color: var(--lb-primary);
  }

  .librebot-slots {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .librebot-slot {
    padding: 10px 8px;
    border: 1px solid var(--lb-border);
    border-radius: 8px;
    background: var(--lb-bg-secondary);
    color: var(--lb-text);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
  }

  .librebot-slot:hover {
    border-color: var(--lb-primary);
  }

  .librebot-slot.selected {
    background: var(--lb-primary);
    border-color: var(--lb-primary);
    color: white;
  }

  .librebot-slot:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .librebot-slots-loading,
  .librebot-slots-empty {
    text-align: center;
    padding: 20px;
    color: var(--lb-text-secondary);
    font-size: 13px;
  }

  .librebot-form-field {
    margin-bottom: 16px;
  }

  .librebot-form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--lb-text);
    margin-bottom: 6px;
  }

  .librebot-form-label .required {
    color: #ef4444;
    margin-left: 2px;
  }

  .librebot-form-input,
  .librebot-form-textarea,
  .librebot-form-select {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--lb-border);
    border-radius: 8px;
    background: var(--lb-bg-secondary);
    color: var(--lb-text);
    font-size: 14px;
    outline: none;
    font-family: inherit;
  }

  .librebot-form-input:focus,
  .librebot-form-textarea:focus,
  .librebot-form-select:focus {
    border-color: var(--lb-primary);
  }

  .librebot-form-textarea {
    min-height: 80px;
    resize: vertical;
  }

  .librebot-booking-submit {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 8px;
    background: var(--lb-primary);
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    margin-top: 12px;
  }

  .librebot-booking-submit:hover {
    background: var(--lb-primary-hover);
  }

  .librebot-booking-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Booking Success */
  .librebot-booking-success {
    text-align: center;
    padding: 24px 16px;
  }

  .librebot-booking-success-icon {
    width: 60px;
    height: 60px;
    margin: 0 auto 16px;
    background: var(--lb-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .librebot-booking-success-icon svg {
    width: 32px;
    height: 32px;
    fill: white;
  }

  .librebot-booking-success h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--lb-text);
    margin: 0 0 16px;
  }

  .librebot-booking-details {
    background: var(--lb-bg-secondary);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    text-align: left;
  }

  .librebot-booking-detail {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--lb-border);
    font-size: 13px;
  }

  .librebot-booking-detail:last-child {
    border-bottom: none;
  }

  .librebot-booking-detail-label {
    color: var(--lb-text-secondary);
  }

  .librebot-booking-detail-value {
    color: var(--lb-text);
    font-weight: 500;
  }

  .librebot-meeting-link {
    display: inline-block;
    padding: 12px 24px;
    background: var(--lb-primary);
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 600;
    margin-bottom: 16px;
    transition: background 0.2s;
  }

  .librebot-meeting-link:hover {
    background: var(--lb-primary-hover);
  }

  /* RTL Booking Styles */
  .librebot-widget.rtl .librebot-booking-header {
    flex-direction: row-reverse;
  }

  .librebot-widget.rtl .librebot-form-label .required {
    margin-left: 0;
    margin-right: 2px;
  }

  .librebot-widget.rtl .librebot-form-input,
  .librebot-widget.rtl .librebot-form-textarea {
    text-align: right;
  }

  .librebot-widget.rtl .librebot-booking-detail {
    flex-direction: row-reverse;
  }

  .librebot-widget.rtl .librebot-booking-details {
    text-align: right;
  }
`;
